import { Box, Card, IconButton, Stack } from '@mui/material';
import { Close } from '@mui/icons-material';
import NumericStepInputField from 'components/forms/fields/numeric-step-input-field/numeric-step-input-field.component';
import { useTranslation } from 'react-i18next';
import { useEffect, useState } from 'react';
import { useFormContext } from 'react-hook-form';
import { formatCurrency } from 'util/currency';

type ThrottleConditionCardProps = {
  index: number;
  orderAmount: number;
  processingTime: number;
  onRemove: (index: number) => void;
};

const ThrottleConditionCard = ({
  index,
  orderAmount,
  processingTime,
  onRemove,
}: ThrottleConditionCardProps) => {
  const { t } = useTranslation();
  const { setValue } = useFormContext();
  const [isInitialLoad, setIsInitialLoad] = useState(true);
  const timeText = processingTime === 1 ? 'minute' : 'minutes';

  useEffect(() => {
    if (!isInitialLoad)
      setValue(`throttleSettings.${index}.displayMessage`, {
        EN: `We will need a little more time to prepare your Order. Please select a new time. Estimated prep time for orders exceeding ${formatCurrency(
          orderAmount,
          'USD',
          0,
        )} is ${processingTime} ${timeText}.`,
      });

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [orderAmount, processingTime]);

  useEffect(() => {
    if (isInitialLoad) {
      setIsInitialLoad(false);
    }
  }, [isInitialLoad]);

  return (
    <Card
      sx={{ p: 1 }}
      data-testid={`ValueCondition.ThrottleSettings.Card.${index}`}
    >
      <Stack display="flex" direction="row" alignItems="center" spacing={1}>
        <Box>
          <NumericStepInputField
            name={`throttleSettings.${index}.orderAmount`}
            id={`orderAmount-${index}`}
            label={t(
              'forms.setPreparationTimeForm.fields.throttleSettings.orderAmount.label',
            )}
            step={1}
            allowNegative={false}
            prefix={`$ `}
            decimalScale={2}
            fixedDecimalScale
            decrementDisabled={val => val < 1}
            incrementDisabled={val => val > 999999998}
            data-testid={`ValueCondition.ThrottleSettings.OrderAmount.${index}`}
          />
          <NumericStepInputField
            name={`throttleSettings.${index}.processingTime`}
            id={`processingTime-${index}`}
            label={t(
              'forms.setPreparationTimeForm.fields.throttleSettings.processingTime.label',
            )}
            step={1}
            allowNegative={false}
            allowDecimal={false}
            decrementDisabled={val => val < 2}
            suffix={` ${t(
              'forms.setPreparationTimeForm.fields.throttleSettings.processingTime.suffix',
            )}`}
            data-testid={`ValueCondition.ThrottleSettings.ProcessingTime.${index}`}
          />
        </Box>
        <Box>
          <IconButton
            aria-label="delete"
            onClick={() => onRemove(index)}
            data-testid={`ValueCondition.ThrottleSettings.CardRemove.${index}`}
          >
            <Close />
          </IconButton>
        </Box>
      </Stack>
    </Card>
  );
};

export default ThrottleConditionCard;
